// Per-Form Analytics
// ----------------------------------------------------------------------------
// A single tab that lets the user pick one of the FIVE safety form types from a
// "Forms" dropdown and then renders a DISTINCT set of charts tailored to that
// form, all driven by the real `/v1/forms/analytics` feed (no mock data):
//
//   BBS      → submissions trend · safe-vs-concern by work group · concern
//              rate by work group · top jobs
//   SHP      → audits trend · pass-vs-fail by section · failed items by
//              section · top audit locations
//   WDS      → miles & gallons trend · fuel spend trend · vehicle-vs-equipment
//              split · top inspection issues
//   AIF      → incidents trend · severity mix · incident causes · injured
//              body parts
//   TRAILER  → inspections trend · by company · by status · top jobs
//
// BBS/SHP/WDS/AIF come straight from the parent's already-filtered `rows`, so
// the counts here match the dashboard's stat cards exactly (same source, same
// created_at windowing). Only TRAILER — which the parent does not load — is
// fetched here, and it's run through the IDENTICAL created_at window filter so
// every form is counted the same way. company_name, bbs_meta, shp_meta,
// wds_meta, incident_meta & severity hints all arrive pre-parsed from the API.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import api from "../../api/api";
import AIFFormModal from "../../forms/viewAiF";
import ViewFormModal from "../../forms/ViewFormModal";
import ViewSHPFormModal from "../../forms/ViewSHPModal";
import WDSFormModal from "../../forms/WDSformModal";
import ViewTrailerModal from "../../forms/TrailerForms/ViewTrailerModal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
  LineChart,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";

// ---------------------------------------------------------------------------
// Form catalogue — the 5 options shown in the "Forms" selector.
// ---------------------------------------------------------------------------
const FORMS = [
  { code: "BBS", label: "BBS Card", color: "#155DFC" },
  { code: "SHP", label: "Safety & Health", color: "#00A63E" },
  { code: "WDS", label: "Weekly Driver Log", color: "#E17100" },
  { code: "AIF", label: "Accident / Incident", color: "#E7000B" },
  { code: "TRAILER", label: "Trailer Inspection", color: "#7C3AED" },
];
const PIE_COLORS = ["#155DFC", "#00A63E", "#E17100", "#E7000B", "#7C3AED", "#0EA5E9", "#D97706", "#DC2626"];

// ---------------------------------------------------------------------------
// UI primitives (mirrors correlation.jsx so the two screens feel consistent)
// ---------------------------------------------------------------------------
const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
    {children}
  </div>
);

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-3">
    <h2 className="text-sm font-bold text-[#0A0A0A] text-left uppercase tracking-wide">{title}</h2>
    {subtitle && <p className="text-[11px] text-[#717182] text-left">{subtitle}</p>}
  </div>
);

const KpiTile = ({ label, value, sub, accent = "#0A0A0A" }) => (
  <div className="flex-1 min-w-[150px] rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">{label}</div>
    <div className="mt-1 flex items-baseline gap-2">
      <span className="text-3xl font-bold" style={{ color: accent }}>{value}</span>
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
    </div>
  </div>
);

const EmptyChart = ({ height = 240, message = "No data in range" }) => (
  <div style={{ height }} className="flex items-center justify-center text-xs text-gray-400 text-center px-4">
    {message}
  </div>
);

// Single-line Y-axis tick for category labels — keeps long job/location/issue
// names on one line, truncating with an ellipsis and showing the full text on
// hover instead of wrapping onto multiple squeezed lines.
const SingleLineTick = ({ x, y, payload, max = 28 }) => {
  const label = String(payload?.value ?? "");
  const truncated = label.length > max;
  const text = truncated ? `${label.slice(0, max - 1)}…` : label;
  return (
    <text x={x} y={y} dy={3} textAnchor="end" fontSize={9} fill="#4B5563">
      {text}
      {truncated && <title>{label}</title>}
    </text>
  );
};

// Eye button that opens a "how is this calculated?" explainer popup.
const InfoButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="How is this calculated?"
    aria-label="How is this calculated?"
    className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-[#155DFC] hover:bg-blue-50 transition"
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  </button>
);

// Simple centered modal (backdrop click + ✕ close).
const InfoModal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div
      className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wide">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-5 py-4 space-y-4 text-left text-[13px] text-gray-700">{children}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Mirror the parent dashboard's date rule (created_at first) so windowing and
// monthly grouping stay identical across every tab.
const getRowDate = (row) => {
  if (row.created_at) return dayjs(row.created_at);
  if (row.filled_date) return dayjs(row.filled_date);
  return null;
};

const normalizeJobName = (s) => String(s || "").replace(/-\(\d+\)\s*$/, "").trim();

const jobLabel = (row) => {
  const name = normalizeJobName(row.jobDescription);
  if (row.job_id && Number(row.job_id) > 0) {
    return name ? `${row.job_id} - ${name}` : String(row.job_id);
  }
  return name || "Unknown";
};

// Group rows into monthly buckets and run an accumulator per bucket.
const byMonth = (rows, accInit, accFn) => {
  const m = {};
  rows.forEach((r) => {
    const d = getRowDate(r);
    if (!d) return;
    const key = d.format("YYYY-MM");
    if (!m[key]) m[key] = { key, label: d.format("MMM 'YY"), ...accInit() };
    accFn(m[key], r);
  });
  return Object.values(m).sort((a, b) => a.key.localeCompare(b.key));
};

// Generic "count by string key" → sorted [{name, value}] array, top N.
const topCounts = (pairs, topN = 8) => {
  const m = {};
  pairs.forEach((k) => {
    const key = (k || "").toString().trim();
    if (!key) return;
    m[key] = (m[key] || 0) + 1;
  });
  return Object.entries(m)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, topN);
};

// ---------------------------------------------------------------------------
// Drill-down → form viewer
// ---------------------------------------------------------------------------
// Every chart below is clickable: clicking a bar / slice / month opens the list
// of forms behind that number, and each row opens the real form viewer — the
// same modals the Safety Analytics tabs use.

const EDIT_ROUTES = {
  BBS: (id) => `/bbs-form?mode=edit&id=${id}`,
  SHP: (id) => `/safety-form?mode=edit&id=${id}&returnTo=safety-analytics`,
  WDS: (id) => `/driver-form?mode=edit&id=${id}`,
  AIF: (id) => `/accident-form?mode=edit&id=${id}`,
};

// Charts hold the slim analytics rows; the viewers need the full form, so pull
// it on demand from /v1/forms/get_by_id.
const useFormViewer = () => {
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const open = async (rowid) => {
    try {
      setLoading(true);
      const resp = await api.get("/v1/forms/get_by_id", { params: { rowid } });
      if (resp.status === 200 && resp.data?.[0]) setRow(resp.data[0]);
    } catch (e) {
      console.error("Failed to fetch form", e);
    } finally {
      setLoading(false);
    }
  };

  const close = () => setRow(null);

  const onEdit = () => {
    const route = EDIT_ROUTES[row?.form_type];
    const id = row?.id;
    setRow(null);
    if (route && id) navigate(route(id));
  };

  return { row, loading, open, close, onEdit };
};

const FormViewerHost = ({ viewer }) => {
  const { row, loading, close, onEdit } = viewer;
  const type = row?.form_type;
  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
          <div className="rounded-lg bg-white px-4 py-2 text-sm text-gray-700 shadow">
            Loading form…
          </div>
        </div>
      )}
      {row && type === "BBS" && (
        <ViewFormModal setFormView={close} rowData={row} formType={type} onEdit={onEdit} />
      )}
      {row && type === "SHP" && (
        <ViewSHPFormModal formType={type} rowData={row} setFormView={close} onEdit={onEdit} />
      )}
      {row && type === "WDS" && (
        <WDSFormModal rowData={row} setFormView={close} onEdit={onEdit} />
      )}
      {row && type === "AIF" && (
        <AIFFormModal formType="AIF" rowData={row} formView setFormView={close} onEdit={onEdit} />
      )}
      {row && type === "TRAILER" && (
        <ViewTrailerModal formType="TRAILER" rowData={row} setFormView={close} />
      )}
    </>
  );
};

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// The list of forms behind a clicked chart element.
const DrillModal = ({ title, rows, onView, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={onClose}
  >
    <div
      className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3 sticky top-0 bg-white">
        <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wide">
          {title} ({rows.length})
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-4">
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 py-6 text-center">No forms found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-2 py-1">Date</th>
                  <th className="px-2 py-1">Employee / Observer</th>
                  <th className="px-2 py-1">Job</th>
                  <th className="px-2 py-1">Company</th>
                  <th className="px-2 py-1 text-right">View</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t hover:bg-blue-50 cursor-pointer"
                    onClick={() => onView(r.id)}
                  >
                    <td className="px-2 py-1 whitespace-nowrap">
                      {getRowDate(r)?.format("MMM D, YYYY") || "—"}
                    </td>
                    <td className="px-2 py-1 truncate max-w-[160px]">
                      {r.emp_name || r.observer_name || "—"}
                    </td>
                    <td className="px-2 py-1 truncate max-w-[180px]" title={r.jobDescription || ""}>
                      {jobLabel(r) || "—"}
                    </td>
                    <td className="px-2 py-1">{r.company_name || "—"}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(r.id);
                        }}
                        title="View form"
                        className="bg-[#1E4976] text-white p-1.5 rounded hover:bg-blue-700 inline-flex items-center justify-center"
                      >
                        <EyeIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Shared per-chart drill state + the click helpers each chart needs.
const useDrill = (rows) => {
  const viewer = useFormViewer();
  const [drill, setDrill] = useState(null); // { title, rows }

  const openDrill = (title, matched) => {
    if (!matched || matched.length === 0) return;
    setDrill({ title, rows: matched });
  };

  // Month buckets share the same "MMM 'YY" label the charts plot.
  const openMonth = (label, title) => {
    if (!label) return;
    openDrill(
      `${title} — ${label}`,
      rows.filter((r) => getRowDate(r)?.format("MMM 'YY") === label),
    );
  };

  const node = (
    <>
      {drill && (
        <DrillModal
          title={drill.title}
          rows={drill.rows}
          onView={viewer.open}
          onClose={() => setDrill(null)}
        />
      )}
      <FormViewerHost viewer={viewer} />
    </>
  );

  return { openDrill, openMonth, node };
};

// Recharts click payloads differ per mark — normalise to the datum.
const CLICKABLE = { cursor: "pointer" };
const barDatum = (d) => d?.payload ?? d;

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
const FormsAnalytics = ({ rows = [], from, to, fetchFrom, pmId = null, jobId = null, companyId = null }) => {
  const [selected, setSelected] = useState("BBS");
  const [trailerRows, setTrailerRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // BBS/SHP/WDS/AIF arrive via `rows` (already windowed by the parent). Only
  // TRAILER needs fetching. We request the SAME widened window the parent uses
  // for its own feed, then filter client-side on created_at within [from, to]
  // — exactly how the parent derives its `rows` — so TRAILER counts the same.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = { form_types: "TRAILER" };
        if (fetchFrom || from) params.from = fetchFrom || from;
        if (to) params.to = to;
        if (pmId != null) params.pm_id = pmId;
        // Job Analytics: scope the TRAILER feed to the selected job (and
        // company) so the 5th form stays in sync with the other four.
        if (jobId != null) params.job_id = jobId;
        if (companyId != null) params.companyid = companyId;
        const resp = await api.get("/v1/forms/analytics", { params });
        const data = Array.isArray(resp?.data?.data)
          ? resp.data.data
          : Array.isArray(resp?.data)
          ? resp.data
          : [];
        if (!cancelled) setTrailerRows(data);
      } catch (err) {
        if (!cancelled) setTrailerRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchFrom, from, to, pmId, jobId, companyId]);

  // Apply the parent's created_at window to the TRAILER feed, then merge with
  // the parent's already-filtered rows into one consistent dataset.
  const allRows = useMemo(() => {
    const fromD = from ? dayjs(from) : null;
    const toD = to ? dayjs(to) : null;
    const inWindow = (d) =>
      d &&
      (!fromD || d.isAfter(fromD) || d.isSame(fromD, "day")) &&
      (!toD || d.isBefore(toD) || d.isSame(toD, "day"));
    const trailer = trailerRows.filter(
      (r) => r.form_type === "TRAILER" && inWindow(getRowDate(r)),
    );
    return [...rows, ...trailer];
  }, [rows, trailerRows, from, to]);

  const formRows = useMemo(
    () => allRows.filter((r) => r.form_type === selected),
    [allRows, selected],
  );

  const meta = FORMS.find((f) => f.code === selected) || FORMS[0];

  // ---- Counts per form (for the selector badges) ----
  const countByCode = useMemo(() => {
    const m = {};
    allRows.forEach((r) => { m[r.form_type] = (m[r.form_type] || 0) + 1; });
    return m;
  }, [allRows]);

  // ---- Download the currently-shown per-form section as a PDF ----
  const captureRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    const el = captureRef.current;
    if (!el || formRows.length === 0) return;
    try {
      setDownloading(true);
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: "#F3F4F6",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10; // mm side margin
      const topMargin = 12; // mm gap above the content
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;

      // Top-aligned with a margin; paginate if it's taller than one page.
      let heightLeft = imgH;
      let position = topMargin;
      pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
      heightLeft -= pageH - topMargin;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
        heightLeft -= pageH;
      }

      const range = from && to ? `_${from}_to_${to}` : "";
      pdf.save(`PerForm-${selected}${range}.pdf`);
    } catch (err) {
      console.error("Failed to export analytics PDF", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4 bg-gray-100 p-3 rounded-xl">
      {/* Header + Forms selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h1 className="text-base font-bold text-left">PER-FORM ANALYTICS</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || formRows.length === 0}
            title="Download this section as PDF"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            {downloading ? "Preparing…" : "Download Analytics"}
          </button>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Forms</label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="text-sm font-medium border border-gray-300 rounded-lg bg-white px-3 py-1.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            style={{ color: meta.color }}
          >
            {FORMS.map((f) => (
              <option key={f.code} value={f.code} style={{ color: "#0A0A0A" }}>
                {f.label} ({countByCode[f.code] || 0})
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && selected === "TRAILER" ? (
        <Card><EmptyChart message="Loading…" /></Card>
      ) : formRows.length === 0 ? (
        <Card>
          <EmptyChart message={`No ${meta.label} submissions in the selected range.`} />
        </Card>
      ) : (
        <div ref={captureRef} className="space-y-4">
          {selected === "BBS" && <BbsCharts rows={formRows} />}
          {selected === "SHP" && <ShpCharts rows={formRows} />}
          {selected === "WDS" && <WdsCharts rows={formRows} />}
          {selected === "AIF" && <AifCharts rows={formRows} />}
          {selected === "TRAILER" && <TrailerCharts rows={formRows} />}
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// BBS — behaviour-based safety (uses bbs_meta: bucket → [{label, answer S|C}])
// ===========================================================================
const BbsCharts = ({ rows, singleJob = false }) => {
  const { groups, totalS, totalC } = useMemo(() => {
    const groups = {}; // bucket → { S, C }
    let totalS = 0, totalC = 0;
    rows.forEach((r) => {
      const m = r.bbs_meta;
      if (!m || typeof m !== "object") return;
      Object.entries(m).forEach(([bucket, arr]) => {
        if (!Array.isArray(arr)) return;
        if (!groups[bucket]) groups[bucket] = { S: 0, C: 0 };
        arr.forEach((it) => {
          if (it?.answer === "S") { groups[bucket].S += 1; totalS += 1; }
          else if (it?.answer === "C") { groups[bucket].C += 1; totalC += 1; }
        });
      });
    });
    return { groups, totalS, totalC };
  }, [rows]);

  const trend = useMemo(
    () => byMonth(rows, () => ({ forms: 0 }), (b) => { b.forms += 1; }),
    [rows],
  );

  const groupData = useMemo(
    () => Object.entries(groups).map(([group, v]) => {
      const t = v.S + v.C;
      return {
        group,
        safe: v.S,
        concern: v.C,
        concernRate: t ? +((v.C / t) * 100).toFixed(1) : 0,
      };
    }).sort((a, b) => b.concernRate - a.concernRate),
    [groups],
  );

  const topJobs = useMemo(() => topCounts(rows.map(jobLabel), 8), [rows]);
  // Single-job view: "Top Jobs" is meaningless (one job), so surface the
  // specific behaviours most often flagged as a Concern instead.
  const topConcernItems = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const meta = r.bbs_meta;
      if (!meta || typeof meta !== "object") return;
      Object.values(meta).forEach((arr) => {
        if (!Array.isArray(arr)) return;
        arr.forEach((it) => {
          if (it?.answer === "C" && it?.label) m[it.label] = (m[it.label] || 0) + 1;
        });
      });
    });
    return Object.entries(m)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [rows]);
  const totalObs = totalS + totalC;
  const concernPct = totalObs ? ((totalC / totalObs) * 100).toFixed(1) : "0.0";

  const { openDrill, openMonth, node: drillNode } = useDrill(rows);

  // Forms that contain the clicked work group.
  const formsInGroup = (group) =>
    rows.filter((r) => Array.isArray(r.bbs_meta?.[group]));

  // Forms that flagged the clicked behaviour as a Concern.
  const formsWithConcern = (label) =>
    rows.filter((r) =>
      Object.values(r.bbs_meta || {}).some(
        (arr) => Array.isArray(arr) && arr.some((it) => it?.answer === "C" && it?.label === label),
      ),
    );

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total BBS Forms" value={rows.length} accent="#155DFC" />
        <KpiTile label="Safe Observations" value={totalS.toLocaleString()} accent="#16A34A" />
        <KpiTile label="Concern Observations" value={totalC.toLocaleString()} accent="#DC2626" />
        <KpiTile label="Concern Rate" value={`${concernPct}%`} accent="#D97706" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="BBS Submissions Over Time" subtitle="Click a month to view its forms" />
          {trend.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={trend}
                style={CLICKABLE}
                onClick={(st) => openMonth(st?.activeLabel, "BBS forms")}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="forms" name="Forms" stroke="#155DFC" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="forms" position="top" style={{ fontSize: 9 }} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Safe vs Concern by Work Group" />
          {groupData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={groupData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="group" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="safe" name="Safe" stackId="a" fill="#16A34A" cursor="pointer"
                  onClick={(d) => {
                    const g = barDatum(d)?.group;
                    openDrill(`BBS forms — ${g}`, formsInGroup(g));
                  }}
                />
                <Bar
                  dataKey="concern" name="Concern" stackId="a" fill="#DC2626" cursor="pointer"
                  onClick={(d) => {
                    const g = barDatum(d)?.group;
                    openDrill(`BBS forms — ${g}`, formsInGroup(g));
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Concern Rate % by Work Group" />
          {groupData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={groupData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" unit="%" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="group" width={130} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar
                  dataKey="concernRate" fill="#D97706" radius={[0, 4, 4, 0]} cursor="pointer"
                  onClick={(d) => {
                    const g = barDatum(d)?.group;
                    openDrill(`BBS forms — ${g}`, formsInGroup(g));
                  }}
                >
                  <LabelList dataKey="concernRate" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle
            title={singleJob ? "Top Concern Items" : "Top Jobs by Submissions"}
            subtitle={singleJob ? "Behaviours most often flagged as a concern" : undefined}
          />
          {(singleJob ? topConcernItems : topJobs).length === 0 ? (
            <EmptyChart message={singleJob ? "No concerns flagged" : "No data in range"} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={singleJob ? topConcernItems : topJobs} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar
                  dataKey="value" name={singleJob ? "Concerns" : "Forms"}
                  fill={singleJob ? "#DC2626" : "#155DFC"} cursor="pointer"
                  onClick={(d) => {
                    const name = barDatum(d)?.name;
                    if (!name) return;
                    openDrill(
                      singleJob ? `Forms flagging “${name}”` : `BBS forms — ${name}`,
                      singleJob
                        ? formsWithConcern(name)
                        : rows.filter((r) => jobLabel(r) === name),
                    );
                  }}
                >
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
      {drillNode}
    </>
  );
};

// ===========================================================================
// SHP — safety & health audits (uses shp_meta: buckets YES/NO/N/A, infractions)
// ===========================================================================
const ShpCharts = ({ rows }) => {
  const { sections, totalPass, totalFail, infractions } = useMemo(() => {
    const sections = {}; // section → { yes, no }
    let totalPass = 0, totalFail = 0, infractions = 0;
    rows.forEach((r) => {
      const m = r.shp_meta;
      if (!m || typeof m !== "object") return;
      infractions += Number(m.infractions_count || 0);
      const buckets = m.buckets || {};
      Object.entries(buckets).forEach(([section, arr]) => {
        if (!Array.isArray(arr)) return;
        if (!sections[section]) sections[section] = { yes: 0, no: 0 };
        arr.forEach((it) => {
          const a = (it?.answer || "").toUpperCase();
          if (a === "YES") { sections[section].yes += 1; totalPass += 1; }
          else if (a === "NO") { sections[section].no += 1; totalFail += 1; }
        });
      });
    });
    return { sections, totalPass, totalFail, infractions };
  }, [rows]);

  const trend = useMemo(
    () => byMonth(rows, () => ({ audits: 0 }), (b) => { b.audits += 1; }),
    [rows],
  );

  const sectionData = useMemo(
    () => Object.entries(sections).map(([section, v]) => {
      const t = v.yes + v.no;
      return { section, pass: v.yes, fail: v.no, failRate: t ? +((v.no / t) * 100).toFixed(1) : 0 };
    }).sort((a, b) => b.fail - a.fail),
    [sections],
  );

  const failedBySection = useMemo(
    () => sectionData.filter((d) => d.fail > 0).map((d) => ({ name: d.section, value: d.fail })),
    [sectionData],
  );

  const topLocations = useMemo(
    () => topCounts(rows.map((r) => r.shp_meta?.location || r.company_name), 8),
    [rows],
  );

  const totalItems = totalPass + totalFail;
  const passPct = totalItems ? ((totalPass / totalItems) * 100).toFixed(1) : "0.0";

  const { openDrill, openMonth, node: drillNode } = useDrill(rows);

  const formsInSection = (section) =>
    rows.filter((r) => Array.isArray(r.shp_meta?.buckets?.[section]));

  const formsFailingSection = (section) =>
    rows.filter((r) =>
      (r.shp_meta?.buckets?.[section] || []).some(
        (it) => (it?.answer || "").toUpperCase() === "NO",
      ),
    );

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total Audits" value={rows.length} accent="#00A63E" />
        <KpiTile label="Failed Items" value={totalFail.toLocaleString()} accent="#DC2626" />
        <KpiTile label="Pass Rate" value={`${passPct}%`} accent="#16A34A" />
        <KpiTile label="Infractions" value={infractions.toLocaleString()} accent="#D97706" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="Audits Over Time" subtitle="Click a month to view its audits" />
          {trend.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={trend}
                style={CLICKABLE}
                onClick={(st) => openMonth(st?.activeLabel, "Audits")}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="audits" name="Audits" stroke="#00A63E" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="audits" position="top" style={{ fontSize: 9 }} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Pass vs Fail by Section" />
          {sectionData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={sectionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="section" tick={{ fontSize: 8 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="pass" name="Pass" stackId="a" fill="#16A34A" cursor="pointer"
                  onClick={(d) => {
                    const s = barDatum(d)?.section;
                    openDrill(`Audits — ${s}`, formsInSection(s));
                  }}
                />
                <Bar
                  dataKey="fail" name="Fail" stackId="a" fill="#DC2626" cursor="pointer"
                  onClick={(d) => {
                    const s = barDatum(d)?.section;
                    openDrill(`Audits failing ${s}`, formsFailingSection(s));
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Failed Items by Section" />
          {failedBySection.length === 0 ? <EmptyChart message="No failed items in range" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={failedBySection} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar
                  dataKey="value" name="Failed" fill="#DC2626" cursor="pointer"
                  onClick={(d) => {
                    const s = barDatum(d)?.name;
                    openDrill(`Audits failing ${s}`, formsFailingSection(s));
                  }}
                >
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Top Audit Locations" />
          {topLocations.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topLocations} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar
                  dataKey="value" name="Audits" fill="#00A63E" cursor="pointer"
                  onClick={(d) => {
                    const loc = barDatum(d)?.name;
                    openDrill(
                      `Audits — ${loc}`,
                      rows.filter(
                        (r) => (r.shp_meta?.location || r.company_name) === loc,
                      ),
                    );
                  }}
                >
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
      {drillNode}
    </>
  );
};

// ===========================================================================
// WDS — weekly driver log (uses wds_meta: miles, gallons, amount, mpg, issues)
// ===========================================================================
const WdsCharts = ({ rows }) => {
  const totals = useMemo(() => {
    let miles = 0, gallons = 0, amount = 0, fillups = 0;
    let vehGal = 0, vehAmt = 0, eqGal = 0, eqAmt = 0;
    rows.forEach((r) => {
      const m = r.wds_meta;
      if (!m || typeof m !== "object") return;
      miles += Number(m.total_miles || 0);
      gallons += Number(m.total_gallons || 0);
      amount += Number(m.total_amount || 0);
      fillups += Number(m.fillup_count || 0);
      vehGal += Number(m.vehicle?.gallons || 0);
      vehAmt += Number(m.vehicle?.amount || 0);
      eqGal += Number(m.equipment?.gallons || 0);
      eqAmt += Number(m.equipment?.amount || 0);
    });
    const avgMpg = gallons > 0 ? +(miles / gallons).toFixed(2) : 0;
    return { miles, gallons, amount, fillups, vehGal, vehAmt, eqGal, eqAmt, avgMpg };
  }, [rows]);

  const trend = useMemo(
    () => byMonth(
      rows,
      () => ({ miles: 0, gallons: 0, amount: 0 }),
      (b, r) => {
        const m = r.wds_meta || {};
        b.miles += Number(m.total_miles || 0);
        b.gallons += Number(m.total_gallons || 0);
        b.amount += Number(m.total_amount || 0);
      },
    ).map((b) => ({ ...b, miles: +b.miles.toFixed(1), gallons: +b.gallons.toFixed(1), amount: +b.amount.toFixed(2) })),
    [rows],
  );

  const splitData = useMemo(() => {
    const out = [];
    if (totals.vehGal > 0) out.push({ name: "Vehicle", value: +totals.vehGal.toFixed(1) });
    if (totals.eqGal > 0) out.push({ name: "Equipment", value: +totals.eqGal.toFixed(1) });
    return out;
  }, [totals]);

  const topIssues = useMemo(() => {
    const all = [];
    rows.forEach((r) => {
      const issues = r.wds_meta?.issues;
      if (Array.isArray(issues)) all.push(...issues);
    });
    return topCounts(all, 8);
  }, [rows]);

  const { openDrill, openMonth, node: drillNode } = useDrill(rows);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiTile label="Total Miles" value={totals.miles.toLocaleString(undefined, { maximumFractionDigits: 0 })} accent="#E17100" />
        <KpiTile label="Total Gallons" value={totals.gallons.toLocaleString(undefined, { maximumFractionDigits: 0 })} accent="#0A0A0A" />
        <KpiTile label="Fuel Spend" value={`$${totals.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} accent="#16A34A" />
        <KpiTile label="Avg MPG" value={totals.avgMpg} accent="#155DFC" />
        <KpiTile label="Fill-ups" value={totals.fillups.toLocaleString()} accent="#0A0A0A" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="Miles & Gallons Over Time" subtitle="Click a month to view its logs" />
          {trend.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart
                data={trend}
                style={CLICKABLE}
                onClick={(st) => openMonth(st?.activeLabel, "Driver logs")}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="miles" name="Miles" fill="#E17100" />
                <Line yAxisId="right" type="monotone" dataKey="gallons" name="Gallons" stroke="#155DFC" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Fuel Spend Over Time" subtitle="USD · click a month to view its logs" />
          {trend.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={trend}
                style={CLICKABLE}
                onClick={(st) => openMonth(st?.activeLabel, "Driver logs")}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Bar dataKey="amount" name="Fuel $" fill="#16A34A">
                  <LabelList dataKey="amount" position="top" formatter={(v) => `$${Math.round(v)}`} style={{ fontSize: 9 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Vehicle vs Equipment" subtitle="Gallons" />
          {splitData.length === 0 ? <EmptyChart message="No fuel allocation data" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={splitData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={90} label={(e) => `${e.name}: ${e.value}`} cursor="pointer"
                  onClick={(d) => {
                    const kind = barDatum(d)?.name;
                    const key = kind === "Equipment" ? "equipment" : "vehicle";
                    openDrill(
                      `Driver logs with ${kind} fuel`,
                      rows.filter((r) => (Number(r.wds_meta?.[key]?.entries) || 0) > 0),
                    );
                  }}
                >
                  {splitData.map((d, i) => (
                    <Cell key={i} fill={d.name === "Vehicle" ? "#E17100" : "#7C3AED"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Top Inspection Issues" subtitle="Vehicle Safety Check failures" />
          {topIssues.length === 0 ? <EmptyChart message="No inspection failures in range" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topIssues} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar
                  dataKey="value" name="Failures" fill="#DC2626" cursor="pointer"
                  onClick={(d) => {
                    const issue = barDatum(d)?.name;
                    openDrill(
                      `Logs reporting “${issue}”`,
                      rows.filter((r) => (r.wds_meta?.issues || []).includes(issue)),
                    );
                  }}
                >
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
      {drillNode}
    </>
  );
};

// ===========================================================================
// AIF — accident / incident (uses severity_hint + incident_meta)
// ===========================================================================
// Map an AIF row to its display severity label (matches the pie's buckets).
const severityLabel = (r) => {
  const s = (r.severity_hint || "unclassified").toString();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const AifCharts = ({ rows, singleJob = false }) => {
  const navigate = useNavigate();
  const [showSeverityInfo, setShowSeverityInfo] = useState(false);
  const [drillSeverity, setDrillSeverity] = useState(null); // clicked pie slice
  const [viewRow, setViewRow] = useState(null); // full form for the modal
  const [viewLoading, setViewLoading] = useState(false);

  // The actual AIF forms behind the selected severity slice.
  const drillForms = useMemo(
    () => (drillSeverity ? rows.filter((r) => severityLabel(r) === drillSeverity) : []),
    [rows, drillSeverity],
  );

  // Month drill for the trend chart — the severity pie keeps its own modal.
  const [drillMonth, setDrillMonth] = useState(null);
  const monthForms = useMemo(
    () =>
      drillMonth
        ? rows.filter((r) => getRowDate(r)?.format("MMM 'YY") === drillMonth)
        : [],
    [rows, drillMonth],
  );

  // Fetch the full form (with json_data) and open the AIF viewer — same flow
  // the Incidents / Driver Log tabs use.
  const handleViewForm = async (rowid) => {
    try {
      setViewLoading(true);
      const resp = await api.get("/v1/forms/get_by_id", { params: { rowid } });
      if (resp.status === 200 && resp.data?.[0]) setViewRow(resp.data[0]);
    } catch (e) {
      console.error("Failed to fetch incident", e);
    } finally {
      setViewLoading(false);
    }
  };

  const trend = useMemo(
    () => byMonth(rows, () => ({ incidents: 0 }), (b) => { b.incidents += 1; }),
    [rows],
  );

  const severityData = useMemo(
    () => topCounts(rows.map(severityLabel), 8),
    [rows],
  );

  const causeData = useMemo(
    () => topCounts(rows.map((r) => r.incident_meta?.incident_cause).filter(Boolean), 8),
    [rows],
  );

  const bodyPartData = useMemo(() => {
    const all = [];
    rows.forEach((r) => {
      const bp = r.incident_meta?.body_parts;
      if (Array.isArray(bp)) all.push(...bp);
    });
    return topCounts(all, 8);
  }, [rows]);

  const recordables = useMemo(
    () => rows.filter((r) => ["recordable", "lost time", "fatal"].includes((r.severity_hint || "").toLowerCase())).length,
    [rows],
  );

  // Single-job view: "Jobs Affected" is always 1, so show distinct injured
  // body parts instead — a more telling number for one job.
  const distinctBodyParts = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => (r.incident_meta?.body_parts || []).forEach((p) => p && s.add(p)));
    return s.size;
  }, [rows]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total Incidents" value={rows.length} accent="#E7000B" />
        <KpiTile label="Recordable+" value={recordables} accent="#DC2626" />
        <KpiTile label="Distinct Causes" value={causeData.length} accent="#D97706" />
        {singleJob ? (
          <KpiTile label="Body Parts Injured" value={distinctBodyParts} accent="#0A0A0A" />
        ) : (
          <KpiTile label="Jobs Affected" value={new Set(rows.map(jobLabel)).size} accent="#0A0A0A" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="Incidents Over Time" subtitle="Click a month to view its reports" />
          {trend.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={trend}
                style={CLICKABLE}
                onClick={(st) => st?.activeLabel && setDrillMonth(st.activeLabel)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="incidents" name="Incidents" fill="#E7000B" cursor="pointer">
                  <LabelList dataKey="incidents" position="top" style={{ fontSize: 9 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <SectionTitle title="Severity Mix" subtitle="Click a slice to view the forms" />
            <InfoButton onClick={() => setShowSeverityInfo(true)} />
          </div>
          {severityData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={severityData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  onClick={(d) => d && setDrillSeverity(d.name)}
                  style={{ cursor: "pointer" }}
                  label={(e) => `${e.name}: ${e.value}`}
                >
                  {severityData.map((d, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Incident Causes" />
          {causeData.length === 0 ? <EmptyChart message="No cause data in range" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={causeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar dataKey="value" name="Incidents" fill="#D97706">
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Injured Body Parts" />
          {bodyPartData.length === 0 ? <EmptyChart message="No body-part data in range" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={bodyPartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar dataKey="value" name="Reports" fill="#E7000B">
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {showSeverityInfo && (
        <InfoModal title="How Severity Mix works" onClose={() => setShowSeverityInfo(false)}>
          <p className="text-[12px] text-gray-500">
            Each Accident/Incident (AIF) form is tagged with a severity so you can
            see how serious the incidents in this range were.
          </p>

          {/* Live data for the current range */}
          <div>
            <div className="font-bold text-[#0A0A0A]">Breakdown (this range)</div>
            <p className="mt-0.5 text-[12px] text-gray-500">
              {rows.length} incident{rows.length === 1 ? "" : "s"} total — click a
              row to see the actual forms.
            </p>
            <div className="mt-1.5 space-y-1">
              {severityData.map((s, i) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => { setShowSeverityInfo(false); setDrillSeverity(s.name); }}
                  className="w-full flex items-center justify-between rounded-lg border border-gray-100 px-3 py-1.5 text-[12px] hover:bg-blue-50 hover:border-blue-200 transition"
                >
                  <span className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    {s.name}
                  </span>
                  <span className="flex items-center gap-2 text-gray-500">
                    <b className="text-[#0A0A0A]">{s.value}</b>
                    <span className="text-[11px] text-[#155DFC]">View forms →</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-bold text-[#0A0A0A]">Where the severity comes from</div>
            <p className="mt-0.5">
              The <b>Classification (check all that apply)</b> boxes ticked on
              the incident form. When more than one is ticked, the most severe
              wins:
            </p>
            <ol className="mt-1.5 space-y-1 list-decimal ml-5 text-[12px]">
              <li><b>Fatal</b></li>
              <li><b>Lost time</b></li>
              <li><b>Recordable</b></li>
              <li><b>Medical treatment</b></li>
              <li><b>First aid</b></li>
              <li><b>Near miss</b></li>
              <li><b>Motor vehicle accident</b></li>
              <li><b>Environmental incident</b></li>
              <li><b>Property damage</b></li>
              <li><b>Report only</b></li>
            </ol>
            <p className="mt-1.5 text-[12px] text-gray-500">
              An incident with no classification ticked shows as
              <b> Unclassified</b>.
            </p>
          </div>

          <div>
            <div className="font-bold text-[#0A0A0A]">Reading a slice</div>
            <p className="mt-0.5">
              "Lost time: 8" means 8 incident forms in this date range have the
              <b> Lost Time</b> box ticked (and not Fatal, which outranks it).
              Click any slice to see those exact forms.
            </p>
          </div>

          <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-3">
            The "Recordable+" KPI counts Fatal + Lost time + Recordable together.
          </p>
        </InfoModal>
      )}

      {/* Drill-down: the reports behind a clicked month */}
      {drillMonth && (
        <DrillModal
          title={`Incident reports — ${drillMonth}`}
          rows={monthForms}
          onView={handleViewForm}
          onClose={() => setDrillMonth(null)}
        />
      )}

      {/* Drill-down: the actual forms behind a clicked severity slice */}
      {drillSeverity && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDrillSeverity(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wide">
                {drillSeverity} incidents ({drillForms.length})
              </h3>
              <button
                type="button"
                onClick={() => setDrillSeverity(null)}
                aria-label="Close"
                className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {drillForms.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No forms found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="px-2 py-1">Date</th>
                        <th className="px-2 py-1">Job</th>
                        <th className="px-2 py-1">Company</th>
                        <th className="px-2 py-1">Cause</th>
                        <th className="px-2 py-1 text-right">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillForms.map((r) => (
                        <tr key={r.id} className="border-t">
                          <td className="px-2 py-1 whitespace-nowrap">
                            {getRowDate(r)?.format("MMM D, YYYY") || "—"}
                          </td>
                          <td className="px-2 py-1 truncate max-w-[160px]" title={r.jobDescription || ""}>
                            {r.jobDescription || "—"}
                          </td>
                          <td className="px-2 py-1">{r.company_name || "—"}</td>
                          <td className="px-2 py-1">{r.incident_meta?.incident_cause || "—"}</td>
                          <td className="px-2 py-1 text-right">
                            <button
                              type="button"
                              onClick={() => handleViewForm(r.id)}
                              title="View incident report"
                              className="bg-[#1E4976] text-white p-1.5 rounded hover:bg-blue-700 inline-flex items-center justify-center"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
          <div className="rounded-lg bg-white px-4 py-2 text-sm text-gray-700 shadow">Loading form…</div>
        </div>
      )}

      {viewRow && (
        <AIFFormModal
          formType="AIF"
          rowData={viewRow}
          formView={!!viewRow}
          setFormView={() => setViewRow(null)}
          onEdit={() => {
            const id = viewRow.id;
            setViewRow(null);
            navigate(`/accident-form?mode=edit&id=${id}`);
          }}
        />
      )}
    </>
  );
};

// ===========================================================================
// TRAILER — trailer inspection (base columns only; no parsed meta from feed)
// ===========================================================================
const TrailerCharts = ({ rows, singleJob = false }) => {
  const trend = useMemo(
    () => byMonth(rows, () => ({ inspections: 0 }), (b) => { b.inspections += 1; }),
    [rows],
  );

  // Single-job view: swap "Top Jobs" for the people doing the inspections.
  const topInspectors = useMemo(
    () => topCounts(rows.map((r) => r.observer_name || r.emp_name), 8),
    [rows],
  );

  const byCompany = useMemo(
    () => topCounts(rows.map((r) => r.company_name || "Unknown"), 8),
    [rows],
  );

  const byStatus = useMemo(() => {
    const m = { Signed: 0, "Pending Signature": 0 };
    rows.forEach((r) => {
      if (r.pm_status === "Y") m.Signed += 1;
      else m["Pending Signature"] += 1;
    });
    return Object.entries(m).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [rows]);

  const topJobs = useMemo(() => topCounts(rows.map(jobLabel), 8), [rows]);
  const signed = byStatus.find((s) => s.name === "Signed")?.value || 0;

  const { openDrill, openMonth, node: drillNode } = useDrill(rows);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total Inspections" value={rows.length} accent="#7C3AED" />
        <KpiTile label="Signed" value={signed} accent="#16A34A" />
        <KpiTile label="Companies" value={byCompany.length} accent="#0A0A0A" />
        {singleJob ? (
          <KpiTile label="Pending Signature" value={rows.length - signed} accent="#D97706" />
        ) : (
          <KpiTile label="Jobs Covered" value={new Set(rows.map(jobLabel)).size} accent="#155DFC" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="Inspections Over Time" subtitle="Click a month to view its inspections" />
          {trend.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={trend}
                style={CLICKABLE}
                onClick={(st) => openMonth(st?.activeLabel, "Inspections")}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="inspections" name="Inspections" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="inspections" position="top" style={{ fontSize: 9 }} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Inspections by Company" />
          {byCompany.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byCompany} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar
                  dataKey="value" name="Inspections" fill="#7C3AED" cursor="pointer"
                  onClick={(d) => {
                    const c = barDatum(d)?.name;
                    openDrill(
                      `Inspections — ${c}`,
                      rows.filter((r) => (r.company_name || "Unknown") === c),
                    );
                  }}
                >
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="Signature Status" />
          {byStatus.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={90} label={(e) => `${e.name}: ${e.value}`} cursor="pointer"
                  onClick={(d) => {
                    const status = barDatum(d)?.name;
                    openDrill(
                      `Inspections — ${status}`,
                      rows.filter((r) =>
                        status === "Signed" ? r.pm_status === "Y" : r.pm_status !== "Y",
                      ),
                    );
                  }}
                >
                  {byStatus.map((d, i) => (
                    <Cell key={i} fill={d.name === "Signed" ? "#16A34A" : "#D97706"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title={singleJob ? "Top Inspectors" : "Top Jobs by Inspections"} />
          {(singleJob ? topInspectors : topJobs).length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={singleJob ? topInspectors : topJobs} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar
                  dataKey="value" name="Inspections"
                  fill={singleJob ? "#7C3AED" : "#155DFC"} cursor="pointer"
                  onClick={(d) => {
                    const name = barDatum(d)?.name;
                    if (!name) return;
                    openDrill(
                      `Inspections — ${name}`,
                      singleJob
                        ? rows.filter((r) => (r.observer_name || r.emp_name) === name)
                        : rows.filter((r) => jobLabel(r) === name),
                    );
                  }}
                >
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
      {drillNode}
    </>
  );
};

// Named exports so other dashboards (e.g. Job Analytics) can lay out every
// form's charts on a single page instead of behind the Forms dropdown.
export { FORMS, BbsCharts, ShpCharts, WdsCharts, AifCharts, TrailerCharts, useDrill };

export default FormsAnalytics;
